import { useCallback, useEffect, useState } from "react";
import { Check, User, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Card, METHODS } from "./_shared";

export const PROPOSAL_THRESHOLD = 3;

export function ProposedMethodsTab() {
  const { apiFetch } = useAuth();
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});  // key=`${host}|${method}` → bool

  const load = useCallback(async () => {
    setErr("");
    try { const d = await apiFetch("/api/admin/proposed-methods"); setList(d.items || []); }
    catch (e) { setErr(e?.message || "Failed to load"); }
  }, [apiFetch]);
  useEffect(() => { load(); }, [load]);

  const act = async (host, method, action) => {
    const key = `${host}|${method}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await apiFetch(`/api/admin/proposed-methods/${action}`, {
        method: "POST",
        body: JSON.stringify({ host, method }),
      });
      // Optimistic: remove the row immediately
      setList((l) => (l || []).filter((r) => !(r.host === host && r.method === method)));
    } catch (e) {
      alert(e?.message || `${action} failed`);
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="proposed-root">
      <Card title="PROPOSED PAYMENT METHODS"
            subtitle="User-claimed methods the server-side verifier couldn't corroborate. Approve (graduates to trusted) or reject (blocks future re-reports). Auto-graduates at 3 distinct reports if you take no action."
            accent="#0B6CF5" testid="proposed-card">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <div className="text-[11px] text-[var(--srs-ink-dim)]">
            {list?.length ?? "…"} pending {list?.length === 1 ? "claim" : "claims"}
          </div>
          <button onClick={load} className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]" data-testid="proposed-refresh">Refresh</button>
        </div>

        {err && <div className="text-xs text-[#EF4444] mb-2" data-testid="proposed-err">{err}</div>}
        {list === null && <div className="text-xs text-[var(--srs-ink-faint)]">Loading…</div>}
        {list?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center" data-testid="proposed-empty">
            🎉 Nothing to review. All claims are either verified or already-trusted.
          </div>
        )}

        <div className="flex flex-col gap-2" data-testid="proposed-list">
          {list?.map((row) => {
            const key = `${row.host}|${row.method}`;
            const pct = Math.min(100, Math.round((row.report_count / PROPOSAL_THRESHOLD) * 100));
            const isNear = row.report_count >= PROPOSAL_THRESHOLD - 1;
            const isBusy = !!busy[key];
            return (
              <div key={key} data-testid={`proposed-row-${row.host}-${row.method}`}
                   className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 rounded-lg">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--srs-ink)]">{row.host}</span>
                    {row.name && row.name !== row.host && (
                      <span className="text-xs text-[var(--srs-ink-faint)]">· {row.name}</span>
                    )}
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[#0B6CF5] text-white">
                      {row.method}
                    </span>
                    {isNear && (
                      <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[#F59E0B] text-white">
                        AUTO-GRADUATES SOON
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[var(--srs-ink-faint)] font-mono whitespace-nowrap">
                    verify: {row.last_verify_status || "?"} · seen {row.observed_count}×
                  </div>
                </div>

                {/* Progress bar toward auto-graduation */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-1.5 rounded-full bg-[var(--srs-line)] overflow-hidden">
                    <div className="h-full transition-all"
                         style={{ width: `${pct}%`, background: isNear ? "#F59E0B" : "#0B6CF5" }} />
                  </div>
                  <span className="text-[10px] font-mono text-[var(--srs-ink-dim)] tabular-nums whitespace-nowrap">
                    {row.report_count} / {PROPOSAL_THRESHOLD}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 items-center justify-between">
                  <div className="text-[10px] text-[var(--srs-ink-faint)] font-mono">
                    Already trusted: {(row.trusted_methods || []).join(", ") || "none"}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => act(row.host, row.method, "approve")}
                            disabled={isBusy}
                            data-testid={`proposed-approve-${row.host}-${row.method}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 text-white rounded transition-colors">
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => act(row.host, row.method, "reject")}
                            disabled={isBusy}
                            data-testid={`proposed-reject-${row.host}-${row.method}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:border-[#EF4444] hover:text-[#EF4444] rounded transition-colors disabled:opacity-50">
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

